import { Boundaries } from "@planzone/core";
import { id, calendarHolidays } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { resource }: Boundaries["retrieveResourceCalendarHolidays"]["request"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToPending(),
          calendarHolidays: { ...calendarHolidays },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToPending(),
          calendarHolidays: { ...calendarHolidays },
        },
      ];
}

function handleSuccess(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["retrieveResourceCalendarHolidays"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToSuccess(),
          calendarHolidays,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToSuccess(),
          calendarHolidays,
        },
      ];
}

function handleFailure(
  state: State,
  { resource }: Boundaries["retrieveResourceCalendarHolidays"]["failure"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToFailure(),
          calendarHolidays: { ...calendarHolidays },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToFailure(),
          calendarHolidays: { ...calendarHolidays },
        },
      ];
}

function handleAssignCalendarHolidaysToResourceRequest(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["assignCalendarHolidaysToResource"]["request"],
): State {
  const index = state.findIndex((res) => res.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { resource, calendarHolidays, request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { resource, calendarHolidays, request: setRequestStateToPending() },
      ];
}

function handleAssignCalendarHolidaysToResourceSuccess(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["assignCalendarHolidaysToResource"]["success"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToSuccess() }
      : res,
  );
}

function handleAssignCalendarToHolidaysFailure(
  state: State,
  { resource }: Boundaries["assignCalendarHolidaysToResource"]["failure"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToFailure() }
      : res,
  );
}
function handleUnAssignCalendarHolidaysFromResourceSuccess(
  state: State,

  resource: Boundaries["unAssignCalendarHolidaysFromResource"]["success"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToSuccess() }
      : res,
  );
}
const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveResourceCalendarHolidaysRequested]: handleRequest,
    [events.retrieveResourceCalendarHolidaysSucceeded]: handleSuccess,
    [events.retrieveResourceCalendarHolidaysFailed]: handleFailure,
    [events.assignCalendarHolidaysToResourceRequested]:
      handleAssignCalendarHolidaysToResourceRequest,
    [events.assignCalendarHolidaysToResourceSucceeded]:
      handleAssignCalendarHolidaysToResourceSuccess,
    [events.assignCalendarHolidaysToResourceFailed]:
      handleAssignCalendarToHolidaysFailure,
    [events.unAssignCalendarHolidaysFromResourceSucceeded]:
      handleUnAssignCalendarHolidaysFromResourceSuccess,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
